import React, { useEffect, useState } from 'react';

export type ToastState = { kind: 'success' | 'error'; msg: string } | null;

/** Ephemeral toast state with auto-dismiss. Pair with the <Toast /> component. */
export function useToast(timeoutMs = 4000) {
  const [toast, setToast] = useState<ToastState>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), timeoutMs);
    return () => clearTimeout(t);
  }, [toast, timeoutMs]);
  return {
    toast,
    showToast: (kind: 'success' | 'error', msg: string) => setToast({ kind, msg }),
    dismissToast: () => setToast(null),
  };
}

const FADE_MS = 300;

/**
 * Renders the toast with an enter animation and a fade-out before it leaves the DOM.
 * Keeps its own copy so it can animate out after the parent clears `toast` (→ null).
 */
export const Toast: React.FC<{ toast: ToastState; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const [render, setRender] = useState<ToastState>(toast);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (toast) {
      setRender(toast);
      setLeaving(false);
      return;
    }
    // parent cleared it → play fade-out, then unmount
    setLeaving(true);
    const t = setTimeout(() => { setRender(null); setLeaving(false); }, FADE_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  if (!render) return null;
  return (
    <div
      role="status"
      onClick={onDismiss}
      className={`toast${leaving ? ' is-leaving' : ''}`}
      style={{ background: render.kind === 'error' ? 'var(--color-danger)' : 'var(--color-success)' }}
    >
      {render.msg}
    </div>
  );
};
